import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';

// Register only the components we need
echarts.use([PieChart, TooltipComponent, SVGRenderer]);

const RadarChart = ({ data, title, color = '#1e3a5f', maxValue = 100 }) => {
  const anglePerSection = 360 / data.length;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 799);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to add custom line breaks to labels
  const formatLabel = (label) => {
    const labelBreaks = {
      'Inflation Risk': 'Inflation\nRisk',
      'Currency Volatility': 'Currency\nVolatility',
      'Political Instability': 'Political\nInstability',
      'Legal and Regulatory Risk': 'Legal and\nRegulatory\nRisk',
      'Physical Climate Risk': 'Physical\nClimate\nRisk',
      'Economic Complexity': 'Economic\nComplexity',
      'External Accounts': 'External\nAccounts',
      'Quality of Governance': 'Quality of\nGovernance',
      'Social Progress': 'Social\nProgress',
      'Fiscal Policy Space': 'Fiscal\nPolicy\nSpace',
      'Climate Resilience': 'Climate\nResilience'
    };
    return labelBreaks[label] || label;
  };

  const option = {
    grid: {
      containLabel: true,
      left: 10,
      right: 10,
      top: 10,
      bottom: 10
    },
    series: [
      // Label series
      {
        type: 'pie',
        radius: [isMobile ? '80%' : '86%', isMobile ? '80%' : '86%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: data.map((item, index) => ({
          value: 1,
          name: formatLabel(item.label)
        })),
        itemStyle: {
          color: 'transparent'
        },
        label: {
          show: true,
          position: 'inside',
          formatter: '{b}',
          fontSize: isMobile ? 9 : 12,
          color: '#405363',
          fontWeight: 600,
          fontFamily: 'Roboto Bold,sans-serif'
        },
        labelLine: {
          show: false
        },
        labelLayout: {
          hideOverlap: false
        },
        silent: true,
        emphasis: {
          disabled: true
        }
      },
      // Reference circle outline (100%)
      {
        type: 'pie',
        radius: ['71%', '72%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: [{ value: 100 }],
        itemStyle: {
          color: '#405363'
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        silent: true
      },
      // Ring at 80%
      {
        type: 'pie',
        radius: ['56.8%', '57.6%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: [{ value: 100 }],
        itemStyle: {
          color: '#5F6E7B'
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        silent: true
      },
      // Ring at 60%
      {
        type: 'pie',
        radius: ['42.4%', '43.2%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: [{ value: 100 }],
        itemStyle: {
          color: '#7D8992'
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        silent: true
      },
      // Ring at 40%
      {
        type: 'pie',
        radius: ['28%', '28.8%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: [{ value: 100 }],
        itemStyle: {
          color: '#9CA3AA'
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        silent: true
      },
      // Ring at 20%
      {
        type: 'pie',
        radius: ['13.6%', '14.4%'],
        center: ['50%', '50%'],
        startAngle: 90,
        data: [{ value: 100 }],
        itemStyle: {
          color: '#BABEC1'
        },
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        silent: true
      },
      // Create individual sectors for each indicator
      ...data.map((item, index) => {
        const startAngle = 90 - (index * anglePerSection);
        const endAngle = 90 - ((index + 1) * anglePerSection);
        const radius = `${(item.value * 100 * 0.72)}%`; // Scale to match 72% max (80% of 90%)

        return {
          type: 'pie',
          radius: [0, radius],
          center: ['50%', '50%'],
          startAngle: startAngle,
          endAngle: endAngle,
          data: [{ value: 1, name: item.label, originalValue: item.value }],
          itemStyle: {
            color: color,
            opacity: 0.6
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          labelLayout: {
            hideOverlap: false,
            moveOverlap: 'shiftY'
          },
          silent: false
        };
      }),
      // Divider lines
      ...data.map((item, index) => {
        const angle = 90 + (index * anglePerSection);
        return {
          type: 'pie',
          radius: [0, '72%'],
          center: ['50%', '50%'],
          startAngle: angle,
          data: [{ value: 0.2 }, { value: 99.9 }],
          itemStyle: {
            color: (params) => params.dataIndex === 0 ? '#BABEC1' : 'transparent'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        };
      })
    ],
    tooltip: {
      trigger: 'item',
      formatter: function (params) {
        if (params.data && params.data.originalValue !== undefined) {
          return `${params.name}: ${params.data.originalValue.toFixed(2)}`;
        }
        return '';
      }
    }
  };

  return (
    <ReactECharts
      echarts={echarts}
      option={option}
      style={{ width: '100%', height: '450px', padding: '0' }}
      opts={{ renderer: 'svg' }}
      notMerge={true}
    />
  );
};

export default RadarChart;